import { useEffect, useState } from "react";

export interface ViewportGeometry {
  width: number;
  height: number;
  left: number;
  top: number;
}
type VisualGeometry = Pick<
  VisualViewport,
  "width" | "height" | "offsetLeft" | "offsetTop"
>;
export function viewportGeometry(
  viewport: VisualGeometry | null,
  fallback: { innerWidth: number; innerHeight: number },
): ViewportGeometry {
  return {
    width: Math.max(1, viewport?.width ?? fallback.innerWidth),
    height: Math.max(1, viewport?.height ?? fallback.innerHeight),
    left: viewport?.offsetLeft ?? 0,
    top: viewport?.offsetTop ?? 0,
  };
}
export function viewportLayout({
  width,
  height,
}: ViewportGeometry): "portrait" | "landscape" | "short-landscape" | "desktop" {
  if (height > width && width <= 650) return "portrait";
  if (width > height && (height <= 500 || width <= 1000))
    return height <= 350 ? "short-landscape" : "landscape";
  return "desktop";
}
function readViewport(): ViewportGeometry {
  return viewportGeometry(window.visualViewport, window);
}

export function useVisualViewport(): ViewportGeometry {
  const [geometry, setGeometry] = useState(readViewport);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const next = readViewport();
        setGeometry((previous) =>
          previous.width === next.width &&
          previous.height === next.height &&
          previous.left === next.left &&
          previous.top === next.top
            ? previous
            : next,
        );
      });
    };
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    update();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);
  return geometry;
}
